// This is a server-bound floor capability, never a channel, UID or authorization chosen by the browser.
// Same-origin tabs can read the latest generation; each tab keeps its own pending move across navigation.
type Scope = { server: string; subject: string };
type Proof = { token: string; generation: number };
type Move = Proof & { id: string; floor: number };
type Options = { send: (m: unknown) => void; scope: () => Scope | null };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const EPOCH = /^[A-Za-z0-9_-]{1,64}$/;
const count = (v: unknown): v is number => Number.isSafeInteger(v) && (v as number) >= 0;
const proof = (v: any): v is Proof => !!v && typeof v.token === 'string' && UUID.test(v.token) && count(v.generation);

export function createAgoraFloor(options: Options) {
  let enabled = false, recovering = false;
  // null is the original v2 namespace; false refuses storage until a valid server generation arrives.
  let epoch: string | null | false = null, scoped = false;
  let pending: { id: string; resolve: () => void; timer: ReturnType<typeof setTimeout> } | null = null;
  const base = () => {
    const s = options.scope(); if (!s?.server || !s.subject || epoch === false) return null;
    return epoch === null ? `deck.agora.floor.v2:${JSON.stringify([s.server, s.subject])}`
      : `deck.agora.floor.v3:${JSON.stringify([epoch, s.server, s.subject])}`;
  };
  // A generation has a separate key. An old tab cannot overwrite a newer generation between read and write.
  const read = (create: boolean): Proof | null => {
    const b = base(); if (!b) return null;
    try {
      const prefix = `${b}:g:`, found: Proof[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i); if (!k?.startsWith(prefix)) continue;
        const x = JSON.parse(localStorage.getItem(k) || 'null');
        if (proof(x) && k === `${prefix}${x.generation}`) found.push(x);
      }
      found.sort((a, b) => b.generation - a.generation);
      if (found.length) return found[0];
      if (!create || !crypto.randomUUID) return null;
      const initial = { token: crypto.randomUUID(), generation: 0 };
      localStorage.setItem(`${prefix}0`, JSON.stringify(initial)); return initial;
    } catch { return null; }
  };
  const save = (x: Proof) => {
    const b = base(); if (!b || !proof(x) || x.generation < 1) return false;
    try {
      const prefix = `${b}:g:`; localStorage.setItem(`${prefix}${x.generation}`, JSON.stringify(x));
      // Only prune strictly older keys; a stale writer cannot delete a newer proof in another tab.
      const old: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i); if (!k?.startsWith(prefix)) continue;
        const n = Number(k.slice(prefix.length)); if (count(n) && n < x.generation - 7) old.push(k);
      }
      for (const k of old) localStorage.removeItem(k);
      return true;
    } catch { return false; }
  };
  const readMove = (): Move | null => {
    const b = base(); if (!b) return null;
    try {
      const x: unknown = JSON.parse(sessionStorage.getItem(`${b}:move`) || 'null');
      if (!proof(x)) return null;
      const move = x as Move;
      return typeof move.id === 'string' && UUID.test(move.id) && Number.isInteger(move.floor) && move.floor >= 1 && move.floor <= 20 ? move : null;
    } catch { return null; }
  };
  const saveMove = (x: Move | null) => {
    const b = base(); if (!b) return false;
    try { if (x) sessionStorage.setItem(`${b}:move`, JSON.stringify(x)); else sessionStorage.removeItem(`${b}:move`); return true; }
    catch { return false; }
  };
  const finish = () => { if (!pending) return; clearTimeout(pending.timer); const p = pending; pending = null; p.resolve(); };
  return {
    welcome(flags: unknown, serverEpoch?: unknown) {
      enabled = Array.isArray(flags) && flags.includes('voice-agora'); recovering = false; finish();
      if (serverEpoch === undefined && !scoped) epoch = null;
      else {
        scoped = true;
        epoch = typeof serverEpoch === 'string' && EPOCH.test(serverEpoch) ? serverEpoch : false;
      } // invalid configuration or a missing generation after this page saw one: never use v2
    },
    offline() { enabled = false; recovering = false; finish(); if (scoped) epoch = false; },
    request() {
      const x = read(true); if (!x) return {};
      const move = readMove(); if (move && move.generation < x.generation) saveMove(null);
      return { floorToken: x.token, ...(epoch ? { voiceStateEpoch: epoch } : {}),
        ...(move && move.generation === x.generation && move.token === x.token ? { moveId: move.id } : {}),
        ...(recovering ? { recover: true } : {}) };
    },
    recover() { if (enabled) recovering = true; },
    prepare(floor: number): Promise<void> | undefined {
      if (!enabled || !Number.isInteger(floor) || floor < 1 || floor > 20) return;
      const x = read(true); if (!x) return;
      finish(); let id: string; try { id = crypto.randomUUID(); } catch { return; }
      if (!saveMove({ ...x, id, floor })) return;
      return new Promise((resolve) => {
        const timer = setTimeout(finish, 2500); pending = { id, resolve, timer };
        try { options.send({ t: 'agora_move', id, floor, floorToken: x.token, ...(epoch ? { voiceStateEpoch: epoch } : {}) }); } catch { finish(); }
      });
    },
    message(m: any) {
      if (!enabled || !m || typeof m !== 'object') return;
      if (m.t === 'agora_move_result') {
        const move = readMove(); if (!move || m.id !== move.id) return;
        const x = read(false);
        if (m.ok === true && x && UUID.test(m.floorToken) && count(m.floorGeneration) && m.floorGeneration > x.generation) {
          if (save({ token: m.floorToken, generation: m.floorGeneration })) saveMove(null);
        } else if (m.ok === false) saveMove(null);
        if (pending?.id === m.id) finish(); return;
      }
      if (m.t === 'agora_space' && UUID.test(m.floorToken) && count(m.floorGeneration)) {
        const x = read(false); if (!x) return;
        if (m.floorGeneration > x.generation) {
          if (save({ token: m.floorToken, generation: m.floorGeneration })) { const move = readMove(); if (move && move.generation < m.floorGeneration) saveMove(null); recovering = false; }
        } else if (m.floorGeneration === x.generation && m.floorToken === x.token) recovering = false;
      }
    },
  };
}
